import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import EditSubscriptionDialog from '../components/EditSubscriptionDialog';

interface Subscription {
  id: string;
  organizationId: string;
  planId: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  seatsPurchased: number;
  seatsUsed: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  canceledAt?: string;
  billingAmount?: number;
  paymentProviderId?: string;
  customerId?: string;
  createdAt: string;
  updatedAt: string;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  plan: {
    id: string;
    name: string;
    description?: string;
    pricePerSeat: number;
    currency: string;
    billingInterval: string;
  };
}

const AdminSubscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const { formatDateOnly } = useDateFormat();

  useEffect(() => {
    checkPlatformAdminAccess();
  }, []);

  const checkPlatformAdminAccess = async () => {
    try {
      setCheckingAccess(true);
      const profile = await apiService.getProfile();
      if (profile.data?.user?.systemRole !== 'PLATFORM_ADMIN') {
        // Redirect to dashboard if not platform admin
        navigate('/dashboard');
        return;
      }
      // User is platform admin, proceed to fetch subscriptions
      fetchSubscriptions();
    } catch (err: any) {
      setError('Failed to verify access. Please try again.');
      navigate('/dashboard');
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllSubscriptions();
      setSubscriptions(response.subscriptions || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedSubscription(null);
  };

  const handleEditSuccess = () => {
    fetchSubscriptions();
    handleEditClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'error';
      case 'incomplete':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      case 'incomplete':
        return 'Incomplete';
      default:
        return status;
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const matchesSearch =
      sub.organization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.organization.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.plan.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || sub.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const columns: GridColDef[] = [
    {
      field: 'organization',
      headerName: 'Organization',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.organization.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.organization.slug}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'plan',
      headerName: 'Plan',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.plan.name}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'seats',
      headerName: 'Seats',
      width: 120,
      renderCell: (params) => {
        const { seatsPurchased, seatsUsed } = params.row;
        const available = seatsPurchased - seatsUsed;
        return (
          <Box>
            <Typography variant="body2">
              <strong>{seatsPurchased}</strong> purchased
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {seatsUsed} used • {available} available
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'billingAmount',
      headerName: 'Monthly Amount',
      width: 130,
      renderCell: (params) => {
        const amount = params.row.billingAmount || 
          (params.row.seatsPurchased * params.row.plan.pricePerSeat);
        return (
          <Typography variant="body2">
            ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      field: 'trialEnd',
      headerName: 'Trial End',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return <Typography variant="body2">{formatDateOnly(params.value)}</Typography>;
      },
    },
    {
      field: 'currentPeriodEnd',
      headerName: 'Next Billing',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return <Typography variant="body2">{formatDateOnly(params.value)}</Typography>;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">{formatDateOnly(params.value)}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
      ],
    },
  ];

  if (checkingAccess || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Subscriptions
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSubscriptions}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by organization or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="trialing">Trialing</MenuItem>
                  <MenuItem value="past_due">Past Due</MenuItem>
                  <MenuItem value="canceled">Canceled</MenuItem>
                  <MenuItem value="incomplete">Incomplete</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Total: {filteredSubscriptions.length}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
        <DataGrid
          rows={filteredSubscriptions}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {selectedSubscription && (
        <EditSubscriptionDialog
          open={editDialogOpen}
          onClose={handleEditClose}
          subscription={selectedSubscription}
          onSuccess={handleEditSuccess}
        />
      )}
    </Box>
  );
};

export default AdminSubscriptions;

